import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { Button } from './ui/Button';

interface TourStop {
  targetRef: React.RefObject<HTMLElement>;
  text: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

interface ProductTourProps {
  stops: TourStop[];
  storageKey?: string;
}

export function ProductTour({
  stops,
  storageKey = 'ww_tour_completed',
}: ProductTourProps) {
  const [activeStop, setActiveStop] = useState(0);
  const [visible, setVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (localStorage.getItem(storageKey) === 'true') return;
    const timer = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(timer);
  }, [storageKey]);

  useEffect(() => {
    if (!visible || !stops[activeStop]?.targetRef.current) return;

    const el = stops[activeStop].targetRef.current;
    const rect = el.getBoundingClientRect();
    const pos = stops[activeStop].position || 'bottom';

    const style: React.CSSProperties = { position: 'fixed', zIndex: 60 };

    if (pos === 'bottom') {
      style.top = rect.bottom + 12;
      style.left = Math.max(16, rect.left + rect.width / 2 - 160);
    } else if (pos === 'top') {
      style.bottom = window.innerHeight - rect.top + 12;
      style.left = Math.max(16, rect.left + rect.width / 2 - 160);
    } else if (pos === 'right') {
      style.top = rect.top + rect.height / 2 - 40;
      style.left = rect.right + 12;
    } else if (pos === 'left') {
      style.top = rect.top + rect.height / 2 - 40;
      style.right = window.innerWidth - rect.left + 12;
    }

    setTooltipStyle(style);
  }, [visible, activeStop, stops]);

  const dismiss = () => {
    localStorage.setItem(storageKey, 'true');
    setVisible(false);
  };

  const next = () => {
    if (activeStop < stops.length - 1) {
      setActiveStop(activeStop + 1);
    } else {
      dismiss();
    }
  };

  if (!visible) return null;

  const currentTarget = stops[activeStop]?.targetRef.current;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={dismiss}
      />

      {currentTarget && (
        <div
          className="fixed z-50 ring-4 ring-primary-400/50 rounded-xl pointer-events-none"
          style={{
            top: currentTarget.getBoundingClientRect().top - 4,
            left: currentTarget.getBoundingClientRect().left - 4,
            width: currentTarget.getBoundingClientRect().width + 8,
            height: currentTarget.getBoundingClientRect().height + 8,
          }}
        />
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={activeStop}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          style={tooltipStyle}
          className="w-80 bg-slate-800 border border-slate-700 rounded-xl p-4 shadow-2xl z-[60]"
        >
          <div className="flex justify-between items-start gap-2 mb-3">
            <p className="text-sm text-slate-200 leading-relaxed">
              {stops[activeStop]?.text}
            </p>
            <button
              onClick={dismiss}
              className="text-slate-500 hover:text-slate-300 flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex justify-between items-center">
            <button
              onClick={dismiss}
              className="text-xs text-slate-500 hover:text-slate-300"
            >
              Skip tour
            </button>

            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">
                {activeStop + 1} / {stops.length}
              </span>
              <Button
                type="button"
                onClick={next}
                className="text-sm px-3 py-1.5"
              >
                {activeStop < stops.length - 1 ? (
                  <>
                    Next
                    <ArrowRight className="w-3 h-3" />
                  </>
                ) : (
                  "Got it!"
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
}
